import React, { useState, useContext, useEffect, Fragment } from "react";
import { UrlServicesContext } from "components/UrlServicesContext";
import { Redirect } from "react-router-dom";
import { makeStyles } from "@mui/styles";
import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import clsx from "clsx";
import axios from "axios";
import Moment from "moment";
import {
  TextField,
  Box,
  Grid,
  Alert,
  Autocomplete,
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Divider,
  List,
  ListItem,
} from "@mui/material";
import {
  Search,
  FileDownload,
  PeopleAlt,
  Close,
  Visibility,
  ContentCopy,
  ReceiptLong,
} from "@mui/icons-material";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import * as XLSX from "sheetjs-style";
import { saveAs } from "file-saver";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";

import loaderGif from "assets/img/loading.gif";
import noDataImg from "assets/img/undraw_House_searching_re_stk8.png";
import ExternalSupplierInvoicesAdmin from "views/dialog/ExternalSupplierInvoicesAdmin";
import {
  cardBodyStyle,
  ButtonTableGrid1,
  ButtonExit,
} from "assets/jss/material-dashboard-react/components/cardBodyStyle";

const useStyles = makeStyles(cardBodyStyle);

const NAVY = "#0B2D5C";
const ORANGE = "#FF7A1A";

// ── Estados: 1=pagado, 0=no procesado, 99=pendiente, 2=caducado ─────────────
const HOURS_TO_EXPIRE = 48;

const STATUS_META = {
  1: { label: "PAGADO", color: "green", severity: "success" },
  0: { label: "NO PROCESADO", color: "red", severity: "error" },
  99: { label: "PENDIENTE", color: "#B45309", severity: "warning" },
  2: { label: "CADUCADO", color: "#6B7585", severity: "info" },
};

const getStatusMeta = (status) =>
  STATUS_META[status] || { label: "—", color: "#333", severity: "info" };

// Chequeo local (además del backend) para no mostrar el botón de copiar
// enlace si ya pasaron 48h, aunque el CRON de expiración aún no haya
// corrido y el status en BD siga en 99.
const isLinkExpired = (row) => {
  if (row.status !== 99) return false;
  const hours = Moment().diff(Moment(row.createdAt), "hours");
  return hours >= HOURS_TO_EXPIRE;
};

const canCopyLink = (row) =>
  row.status === 99 && Boolean(row.slug) && !isLinkExpired(row);

// ── Dialog de detalle de pago ────────────────────────────────────────────────
function PaymentDetailDialog({ open, onClose, row }) {
  // El hook siempre debe llamarse primero, ANTES de cualquier return
  // condicional (regla de hooks: mismo orden en cada render).
  const { urlServices, UserAuth } = useContext(UrlServicesContext);
  const keyAuthorization = UserAuth?.Session;

  if (!row) return null;

  const response = row.paymentResponse ? JSON.parse(row.paymentResponse) : null;
  const payment = response?.payment?.[0];

  const { severity: statusColor, label: statusText } = getStatusMeta(
    row.status,
  );

  const handleGenerateCertificate = async () => {
    try {
      const res = await axios.get(
        `${urlServices}payment-transaction/certificate-external-supplier/${row.id}`,
        { headers: { Authorization: `Bearer ${keyAuthorization}` } },
      );
      window.open(`${urlServices}documents/${res.data.pdf}`, "_blank");
    } catch (e) {
      console.error("Error generando certificado:", e.message);
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      keepMounted
      PaperProps={{ sx: { borderRadius: 6 } }}
    >
      <DialogContent dividers>
        <Grid container>
          <Grid item xs={12} sm={8}>
            <Typography
              sx={{ fontWeight: 700, fontSize: 24, color: NAVY, mb: 1 }}
            >
              Detalle del registro y pago
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <ButtonTableGrid1
              onClick={(e) => {
                e.stopPropagation();
                handleGenerateCertificate();
              }}
              style={{ margin: "12px 0", background: "green", float: "right" }}
            >
              Certificado
            </ButtonTableGrid1>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 2 }} />

        {/* Datos del cliente */}
        <Typography sx={{ fontWeight: 700, fontSize: 16, color: NAVY, mb: 1 }}>
          Datos del cliente
        </Typography>
        <Grid container spacing={1} sx={{ mb: 2 }}>
          {[
            ["Nombre", `${row.nameClient} ${row.lastnameClient}`],
            [
              "Documento",
              `${row.typesDocument?.toUpperCase()}: ${row.document}`,
            ],
            ["Email", row.emailClient],
            ["Teléfono", row.phoneClient],
            ["Placa", row.placaClient],
            ["Dirección", row.addressClient],
            ["Asesor", row.advisor?.name || "—"],
            ["Producto", row.product?.name || "—"],
            ["Empresa", row.company?.name || "—"],
            ["Registro", Moment(row.createdAt).format("DD/MM/YYYY HH:mm")],
          ].map(([label, val]) => (
            <Grid item xs={12} sm={6} key={label}>
              <Typography sx={{ fontSize: 13 }}>
                <b style={{ color: NAVY }}>{label}: </b>
                {val}
              </Typography>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ mb: 2 }} />

        {/* Estado del pago */}
        <Typography sx={{ fontWeight: 700, fontSize: 16, color: NAVY, mb: 1 }}>
          Estado del pago
        </Typography>
        <Alert severity={statusColor} sx={{ mb: 2, fontWeight: "bold" }}>
          {statusText}
        </Alert>

        {row.status === 2 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            El enlace de pago caducó a las 48 horas de haberse generado sin
            completar el pago. Se debe registrar nuevamente al cliente para
            generar un enlace vigente.
          </Alert>
        )}

        {payment && (
          <List
            sx={{
              border: "1px solid #ddd",
              borderRadius: 2,
              py: 0,
              width: "100%",
            }}
          >
            {[
              ["Referencia", payment.reference],
              [
                "Fecha",
                Moment(payment.status?.date).format("DD/MM/YYYY HH:mm"),
              ],
              ["Valor", `$${response?.request?.payment?.amount?.total}`],
              ["Recibo", payment.receipt],
              ["Autorización", payment.authorization],
              ["Franquicia", payment.issuerName],
              ["Método", payment.paymentMethodName],
            ]
              .filter(([, v]) => v)
              .map(([label, val]) => (
                <Fragment key={label}>
                  <ListItem sx={{ py: 0.5 }}>
                    <Grid container>
                      <Grid item xs={5}>
                        <b style={{ fontSize: 13 }}>{label}</b>
                      </Grid>
                      <Grid
                        item
                        xs={7}
                        sx={{ textAlign: "right", fontSize: 13 }}
                      >
                        {val}
                      </Grid>
                    </Grid>
                  </ListItem>
                  <Divider component="li" />
                </Fragment>
              ))}
          </List>
        )}

        {!payment && row.status === 99 && (
          <Alert severity="warning">
            El cliente aún no ha completado el proceso de pago.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <ButtonExit onClick={onClose}>Cerrar</ButtonExit>
      </DialogActions>
    </Dialog>
  );
}

// ── Componente principal ─────────────────────────────────────────────────────
export default function ExternalSupplierListAdmin() {
  const classes = useStyles();
  const { urlServices, UserAuth } = useContext(UrlServicesContext);
  const keyAuthorization = UserAuth.Session;

  const [rows, setRows] = useState([]);
  const [rowsAll, setRowsAll] = useState([]);
  const [advisors, setAdvisors] = useState([]);
  const [loader, setLoader] = useState(true);
  const [error, setError] = useState(false);
  const [mensageSystem, setMensageSystem] = useState("");
  const [returnLogin, setReturnLogin] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedAdvisor, setSelectedAdvisor] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [openDetail, setOpenDetail] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [copiedRowId, setCopiedRowId] = useState(null);
  const [openInvoices, setOpenInvoices] = useState(false);
  const [invoiceRow, setInvoiceRow] = useState(null);

  const handleCopyLink = (row) => {
    if (!row.slug) return;
    const link = `${window.location.origin}/paymentayasa/${row.slug}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopiedRowId(row.id);
      setTimeout(() => setCopiedRowId(null), 2000);
    });
  };

  const columns = [
    {
      headerName: "Cliente",
      field: "nameClient",
      flex: 1,
      minWidth: 220,
      headerClassName: "super-app-theme--header",
      cellClassName: (p) => clsx("super-app", { celAll: p.value }),
      renderCell: (p) => (
        <div
          style={{ display: "flex", flexDirection: "column", padding: "6px 0" }}
        >
          <span style={{ fontSize: 13, fontWeight: "bold", color: "#000" }}>
            {p.row.nameClient} {p.row.lastnameClient}
          </span>
          <span style={{ fontSize: 10, color: "#64748B", marginTop: 2 }}>
            {p.row.typesDocument?.toUpperCase()}: {p.row.document}
          </span>
          <span style={{ fontSize: 10, color: "#64748B" }}>
            {p.row.emailClient}
          </span>
          <span style={{ fontSize: 10, color: "#64748B" }}>
            {p.row.phoneClient}
          </span>
        </div>
      ),
    },
    {
      headerName: "Placa",
      field: "placaClient",
      width: 90,
      headerAlign: "center",
      align: "center",
      headerClassName: "super-app-theme--header",
      renderCell: (p) => (
        <b style={{ fontSize: 12 }}>{p.value?.toUpperCase()}</b>
      ),
    },
    {
      headerName: "Asesor",
      field: "advisor",
      width: 170,
      headerClassName: "super-app-theme--header",
      renderCell: (p) => (
        <span style={{ fontSize: 11 }}>{p.row.advisor?.name || "—"}</span>
      ),
    },
    {
      headerName: "Producto",
      field: "product",
      width: 140,
      headerClassName: "super-app-theme--header",
      renderCell: (p) => (
        <span style={{ fontSize: 11 }}>{p.row.product?.name || "—"}</span>
      ),
    },
    {
      headerName: "Fecha",
      field: "createdAt",
      width: 90,
      headerAlign: "center",
      align: "center",
      headerClassName: "super-app-theme--header",
      renderCell: (p) => (
        <span style={{ fontSize: 12 }}>
          {Moment(p.value).format("DD/MM/YY")}
        </span>
      ),
    },
    {
      headerName: "Estado",
      field: "status",
      width: 105,
      headerAlign: "center",
      align: "center",
      headerClassName: "super-app-theme--header",
      renderCell: (p) => {
        const { label, color } = getStatusMeta(p.value);
        return <b style={{ fontSize: 10, color }}>{label}</b>;
      },
    },
    {
      headerName: "Acciones",
      field: "actions",
      width: 150,
      headerAlign: "center",
      align: "center",
      headerClassName: "super-app-theme--header",
      sortable: false,
      filterable: false,
      renderCell: (p) => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            py: 0.5,
            width: "100%",
          }}
        >
          <ButtonTableGrid1
            onClick={(e) => {
              e.stopPropagation();
              setSelectedRow(p.row);
              setOpenDetail(true);
            }}
            style={{ fontSize: 10 }}
          >
            <Visibility sx={{ fontSize: 14, mr: 0.5 }} /> Ver
          </ButtonTableGrid1>

          {/* Solo se muestra si: sigue pendiente (99), tiene slug, y no
              han pasado 48h desde el registro. Si ya está pagado, rechazado
              o caducado, no aplica compartir enlace. */}
          {canCopyLink(p.row) && UserAuth?.profile_id === 1 && (
            <ButtonTableGrid1
              onClick={(e) => {
                e.stopPropagation();
                handleCopyLink(p.row);
              }}
              style={{ fontSize: 10, background: NAVY, color: "#fff" }}
            >
              <ContentCopy sx={{ fontSize: 13, mr: 0.5 }} />
              {copiedRowId === p.row.id ? "¡Copiado!" : "Copiar enlace"}
            </ButtonTableGrid1>
          )}

          {/* Solo aplica facturación a pagos ya APROBADOS (status === 1) */}
          {p.row.status === 1 && (
            <ButtonTableGrid1
              onClick={(e) => {
                e.stopPropagation();
                setInvoiceRow(p.row);
                setOpenInvoices(true);
              }}
              style={{ fontSize: 10, background: "#2e7d32", color: "#fff" }}
            >
              <ReceiptLong sx={{ fontSize: 13, mr: 0.5 }} />
              Ver factura
            </ButtonTableGrid1>
          )}
        </Box>
      ),
    },
  ];

  // ── Carga de datos ─────────────────────────────────────────────────────────
  const RefreshTable = async () => {
    setLoader(true);
    setSearchText("");
    setSelectedAdvisor(null);
    setFromDate(null);
    setToDate(null);
    try {
      const res = await axios.get(`${urlServices}external-supplier/list`, {
        headers: { Authorization: `Bearer ${keyAuthorization}` },
      });
      setRows(res.data);
      setRowsAll(res.data);

      // Extraer asesores únicos para el combo
      const uniqueAdvisors = [];
      const seen = new Set();
      res.data.forEach((r) => {
        if (r.advisor && !seen.has(r.advisor.id)) {
          seen.add(r.advisor.id);
          uniqueAdvisors.push(r.advisor);
        }
      });
      setAdvisors(uniqueAdvisors.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (e) {
      if (e.response?.status === 401) {
        setMensageSystem("La sesión ha expirado, vuelva a iniciar sesión ...");
        setError(true);
        setTimeout(() => {
          localStorage.clear();
          setReturnLogin(true);
        }, 4000);
      } else {
        setMensageSystem("Error en la consulta con el servidor.");
        setError(true);
      }
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    RefreshTable();
  }, []);

  // ── Filtros ────────────────────────────────────────────────────────────────
  const applyFilters = (search, advisor, from, to, source = rowsAll) => {
    let result = [...source];

    if (search.trim()) {
      const s = search.toLowerCase();
      result = result.filter(
        (r) =>
          `${r.nameClient} ${r.lastnameClient}`.toLowerCase().includes(s) ||
          r.document?.toLowerCase().includes(s) ||
          r.emailClient?.toLowerCase().includes(s) ||
          r.placaClient?.toLowerCase().includes(s),
      );
    }

    if (advisor) {
      result = result.filter((r) => r.advisorId === advisor.id);
    }

    if (from && to) {
      const f = dayjs(from).format("YYYY-MM-DD");
      const t = dayjs(to).format("YYYY-MM-DD");
      result = result.filter((r) => {
        const d = dayjs(r.createdAt).format("YYYY-MM-DD");
        return d >= f && d <= t;
      });
    }

    setRows(result);
  };

  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchText(val);
    applyFilters(val, selectedAdvisor, fromDate, toDate);
  };

  const handleAdvisor = (_, newVal) => {
    setSelectedAdvisor(newVal);
    applyFilters(searchText, newVal, fromDate, toDate);
  };

  const handleFromDate = (val) => {
    setFromDate(val);
    setToDate(null);
  };

  const handleToDate = (val) => {
    setToDate(val);
    applyFilters(searchText, selectedAdvisor, fromDate, val);
  };

  const clearFilters = () => {
    setSearchText("");
    setSelectedAdvisor(null);
    setFromDate(null);
    setToDate(null);
    setRows(rowsAll);
  };

  // ── Exportar Excel ─────────────────────────────────────────────────────────
  const exportExcel = () => {
    const data = rows.map((r) => {
      const response = r.paymentResponse ? JSON.parse(r.paymentResponse) : null;
      const valorPagado = response?.request?.payment?.amount?.total || "—";

      return {
        Nombre: `${r.nameClient} ${r.lastnameClient}`,
        Documento: `${r.typesDocument?.toUpperCase()}: ${r.document}`,
        Correo: r.emailClient,
        Teléfono: r.phoneClient,
        Placa: r.placaClient?.toUpperCase(),
        Dirección: r.addressClient,
        Asesor: r.advisor?.name || "—",
        Producto: r.product?.name || "—",
        "Valor pagado": valorPagado !== "—" ? `${valorPagado}` : "—",
        Empresa: r.company?.name || "—",
        Fecha: Moment(r.createdAt).format("DD/MM/YYYY"),
        Estado: getStatusMeta(r.status).label,
        Referencia: r.paymentReference || "—",
      };
    });

    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = [
      { wch: 28 },
      { wch: 20 },
      { wch: 30 },
      { wch: 14 },
      { wch: 12 },
      { wch: 35 },
      { wch: 30 },
      { wch: 20 },
      { wch: 14 },
      { wch: 25 },
      { wch: 12 },
      { wch: 14 },
      { wch: 14 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Registros");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      `registros_ayasa_${Moment().format("YYYY-MM-DD")}.xlsx`,
    );
  };

  // ── Exportar PDF ───────────────────────────────────────────────────────────
  const exportPDF = () => {
    const win = window.open("", "_blank");
    const tableRows = rows
      .map((r) => {
        const { label, color } = getStatusMeta(r.status);
        return `
      <tr>
        <td>${r.nameClient} ${r.lastnameClient}</td>
        <td>${r.document}</td>
        <td>${r.emailClient}</td>
        <td>${r.phoneClient}</td>
        <td>${r.placaClient?.toUpperCase()}</td>
        <td>${r.advisor?.name || "—"}</td>
        <td>${r.product?.name || "—"}</td>
        <td>${Moment(r.createdAt).format("DD/MM/YYYY")}</td>
        <td style="color:${color};font-weight:bold">${label}</td>
      </tr>`;
      })
      .join("");

    win.document.write(`
      <html><head><title>Registros AYASA</title>
      <style>
        body { font-family: Arial, sans-serif; font-size: 11px; margin: 20px; }
        h2 { color: #0B2D5C; }
        table { width: 100%; border-collapse: collapse; margin-top: 12px; }
        th { background: #0B2D5C; color: #fff; padding: 6px 8px; text-align: left; font-size: 10px; }
        td { padding: 5px 8px; border-bottom: 1px solid #ddd; font-size: 10px; }
        tr:nth-child(even) { background: #f4f6f9; }
        .footer { margin-top: 20px; font-size: 10px; color: #888; }
      </style></head><body>
      <h2>Registros AYASA — ${Moment().format("DD/MM/YYYY HH:mm")}</h2>
      <p>Total de registros: <b>${rows.length}</b></p>
      <table>
        <thead><tr>
          <th>Nombre</th><th>Documento</th><th>Correo</th>
          <th>Teléfono</th><th>Placa</th><th>Asesor</th>
          <th>Producto</th><th>Fecha</th><th>Estado</th>
        </tr></thead>
        <tbody>${tableRows}</tbody>
      </table>
      <div class="footer">Generado por el sistema ÚSAME</div>
      </body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 500);
  };

  // ── Datos para gráficas (semana actual) ───────────────────────────────────
  const getChartData = () => {
    const startOfWeek = dayjs().startOf("week");
    const days = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

    const thisWeek = rowsAll.filter((r) => {
      const d = dayjs(r.createdAt);
      return (
        r.status === 1 && // solo pagados
        d.isAfter(startOfWeek.subtract(1, "day")) &&
        d.isBefore(startOfWeek.add(7, "day"))
      );
    });

    const salesByDay = Array(7).fill(0);
    thisWeek.forEach((r) => {
      const dayIndex = dayjs(r.createdAt).day();
      salesByDay[dayIndex]++;
    });

    const advisorCount = {};
    thisWeek.forEach((r) => {
      if (r.advisor?.name) {
        advisorCount[r.advisor.name] = (advisorCount[r.advisor.name] || 0) + 1;
      }
    });

    const top5 = Object.entries(advisorCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, value], index) => ({
        id: index,
        value,
        label: name.split(" ")[0] + " " + (name.split(" ")[2] || ""),
      }));

    // Total en monto de pagos aprobados de la semana
    const totalAmount = thisWeek.reduce((sum, r) => {
      const response = r.paymentResponse ? JSON.parse(r.paymentResponse) : null;
      const amount = response?.request?.payment?.amount?.total || 0;
      return sum + parseFloat(amount);
    }, 0);

    return {
      days,
      salesByDay,
      top5,
      totalWeek: thisWeek.length,
      totalAmount,
      weekStart: startOfWeek.format("DD/MM/YYYY"),
      weekEnd: startOfWeek.add(6, "day").format("DD/MM/YYYY"),
    };
  };

  const { days, salesByDay, top5, totalWeek, totalAmount, weekStart, weekEnd } =
    getChartData();

  if (returnLogin) return <Redirect to="/" />;

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <div className={classes.cardTitleBlack}>
          <PeopleAlt className={classes.iconFilter} /> Registros AYASA
        </div>
        <Card>
          <CardBody>
            {error && <Alert severity="error">{mensageSystem}</Alert>}
            {loader ? (
              <Grid container>
                <Grid item xs={12}>
                  <center>
                    <img
                      src={loaderGif}
                      alt="loader"
                      style={{ width: 60, margin: "20% 0" }}
                    />
                  </center>
                </Grid>
              </Grid>
            ) : (
              <Fragment>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {/* ── Gráficas semana actual ── */}
                  {!loader && (
                    <Grid item xs={12} md={12}>
                      <Box
                        sx={{
                          mb: 3,
                          p: 2,
                          background: "#fff",
                          borderRadius: 3,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: NAVY,
                            fontSize: 16,
                            mb: 2,
                          }}
                        >
                          Semana actual — {totalWeek} pago
                          {totalWeek !== 1 ? "s realizados" : " realizado"}
                          {" · Total: "}
                          <span style={{ color: ORANGE, fontSize: 18 }}>
                            ${totalAmount.toFixed(2)} USD
                          </span>
                        </Typography>
                        <Grid container spacing={2}>
                          {/* Gráfica de barras — ventas por día */}
                          <Grid item xs={12} md={7}>
                            <Typography
                              sx={{
                                fontSize: 13,
                                color: "#555",
                                mb: 1,
                                fontWeight: 600,
                              }}
                            >
                              Registros por día{" "}
                              <span
                                style={{
                                  fontWeight: 500,
                                  color: "#888",
                                  fontSize: 12,
                                }}
                              >
                                ({weekStart} — {weekEnd})
                              </span>
                            </Typography>
                            <BarChart
                              xAxis={[{ scaleType: "band", data: days }]}
                              series={[
                                {
                                  data: salesByDay,
                                  color: NAVY,
                                },
                              ]}
                              height={220}
                              margin={{
                                top: 10,
                                bottom: 30,
                                left: 30,
                                right: 10,
                              }}
                            />
                          </Grid>

                          {/* Gráfica de pastel — top 5 asesores */}
                          <Grid item xs={12} md={5}>
                            <Typography
                              sx={{
                                fontSize: 13,
                                color: "#555",
                                mb: 1,
                                fontWeight: 600,
                              }}
                            >
                              Top 5 asesores
                            </Typography>
                            {top5.length > 0 ? (
                              <PieChart
                                series={[
                                  {
                                    data: top5,
                                    highlightScope: {
                                      faded: "global",
                                      highlighted: "item",
                                    },
                                    faded: {
                                      innerRadius: 30,
                                      additionalRadius: -30,
                                    },
                                    arcLabel: (item) => `${item.value}`,
                                    arcLabelMinAngle: 20,
                                  },
                                ]}
                                height={220}
                                margin={{
                                  top: 10,
                                  bottom: 10,
                                  left: 10,
                                  right: 120,
                                }}
                                slotProps={{
                                  legend: {
                                    direction: "column",
                                    position: {
                                      vertical: "middle",
                                      horizontal: "right",
                                    },
                                    itemMarkWidth: 10,
                                    itemMarkHeight: 10,
                                    markGap: 5,
                                    itemGap: 8,
                                    labelStyle: { fontSize: 11 },
                                  },
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  height: 220,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "#999",
                                  fontSize: 13,
                                }}
                              >
                                Sin datos esta semana
                              </Box>
                            )}
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>
                  )}
                  {/* Buscador */}
                  <Grid item xs={12} sm={12}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: NAVY,
                        fontSize: 16,
                        mb: 2,
                      }}
                    >
                      Filtros
                    </Typography>
                    <TextField
                      variant="outlined"
                      size="small"
                      fullWidth
                      placeholder="Buscar nombre, documento, correo, placa..."
                      value={searchText}
                      onChange={handleSearch}
                      autoComplete="off"
                      InputProps={{
                        startAdornment: (
                          <Search
                            style={{
                              color: "gray",
                              fontSize: 20,
                              marginRight: 8,
                            }}
                          />
                        ),
                      }}
                      sx={{ width: { xs: "100%", md: "60%" } }}
                    />
                  </Grid>

                  {/* Filtro asesor */}
                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      options={advisors}
                      getOptionLabel={(o) => o.name || ""}
                      value={selectedAdvisor}
                      onChange={handleAdvisor}
                      size="small"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Filtrar por asesor"
                          variant="outlined"
                        />
                      )}
                    />
                  </Grid>

                  {/* Fecha desde */}
                  <Grid item xs={12} sm={3}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DemoContainer components={["DatePicker"]} sx={{ pt: 0 }}>
                        <DatePicker
                          label="Desde"
                          value={fromDate}
                          onChange={handleFromDate}
                          format="DD/MM/YYYY"
                          slotProps={{
                            textField: { size: "small", fullWidth: true },
                          }}
                        />
                      </DemoContainer>
                    </LocalizationProvider>
                  </Grid>

                  {/* Fecha hasta */}
                  <Grid item xs={12} sm={3}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DemoContainer components={["DatePicker"]} sx={{ pt: 0 }}>
                        <DatePicker
                          label="Hasta"
                          value={toDate}
                          onChange={handleToDate}
                          format="DD/MM/YYYY"
                          disabled={!fromDate}
                          minDate={fromDate || undefined}
                          slotProps={{
                            textField: { size: "small", fullWidth: true },
                          }}
                        />
                      </DemoContainer>
                    </LocalizationProvider>
                  </Grid>

                  {/* Limpiar filtros */}
                  <Grid
                    item
                    xs={12}
                    sm={1}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <b
                      style={{ fontSize: 10, color: "red", cursor: "pointer" }}
                      onClick={clearFilters}
                    >
                      <Close sx={{ fontSize: 16, verticalAlign: "middle" }} />{" "}
                      LIMPIAR
                    </b>
                  </Grid>

                  {/* Botones exportar */}
                  <Grid
                    item
                    xs={12}
                    sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}
                  >
                    <ButtonTableGrid1
                      onClick={exportExcel}
                      style={{ background: "green" }}
                      startIcon={<FileDownload style={{ fontSize: 14 }} />}
                    >
                      Excel
                    </ButtonTableGrid1>
                    <ButtonTableGrid1
                      onClick={exportPDF}
                      style={{ background: NAVY, color: "#fff" }}
                      startIcon={<FileDownload style={{ fontSize: 14 }} />}
                    >
                      PDF
                    </ButtonTableGrid1>
                    <b
                      style={{
                        fontSize: 12,
                        alignSelf: "center",
                        color: "#555",
                      }}
                    >
                      {rows.length} registro{rows.length !== 1 ? "s" : ""}
                    </b>
                  </Grid>
                </Grid>

                {/* Tabla */}
                {rows.length > 0 ? (
                  <Box
                    sx={{
                      "& .super-app.celAll": { fontSize: 12 },
                      "& .super-app-theme--header": {
                        backgroundColor: "rgb(242, 242, 242)",
                        fontSize: 12,
                      },
                      mt: 1,
                    }}
                  >
                    <DataGrid
                      rows={rows}
                      columns={columns}
                      initialState={{
                        pagination: {
                          paginationModel: { page: 0, pageSize: 10 },
                        },
                      }}
                      pageSizeOptions={[5, 10, 20, 50]}
                      disableRowSelectionOnClick
                      checkboxSelection={false}
                      onRowClick={(params) => {
                        setSelectedRow(params.row);
                        setOpenDetail(true);
                      }}
                      sx={{
                        [`& .${gridClasses.cell}`]: {
                          display: "flex",
                          alignItems: "center",
                        },
                        [`& .${gridClasses.row}`]: {
                          cursor: "pointer",
                        },
                      }}
                      getRowHeight={() => "auto"}
                      autoWidth
                    />
                  </Box>
                ) : (
                  <center>
                    <img
                      src={noDataImg}
                      alt="sin datos"
                      style={{ width: 300, marginTop: 50 }}
                    />
                    <br />
                    <b style={{ fontSize: 18 }}>
                      No hay registros para mostrar
                    </b>
                  </center>
                )}
              </Fragment>
            )}
          </CardBody>
        </Card>
      </GridItem>

      {/* Dialog detalle */}
      <PaymentDetailDialog
        open={openDetail}
        onClose={() => setOpenDetail(false)}
        row={selectedRow}
      />

      {/* Dialog de facturas */}
      {openInvoices && (
        <ExternalSupplierInvoicesAdmin
          open={openInvoices}
          exit={() => setOpenInvoices(false)}
          row={invoiceRow}
          callBackRefresh={RefreshTable}
        />
      )}
    </GridContainer>
  );
}
